import { gql, useQuery } from '@apollo/client';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { CalloutPriority, CalloutType, PaginationDirection } from '../../__generated__/globalTypes';
import { useUpdateCallout } from '../../hooks/mutations/useUpdateCallout';
import { type IDateRange, PATHS } from '../../paths';
import { useNavigateWithDateRange } from '../../providers/DateRangeProvider';
import CalloutCard, { type Callout } from '../CalloutCard';
import { isUnusualCumulativeSpendMetadata } from '../CalloutCard/cards';
import TransactionSlideout from '../slideouts/TransactionSlideout';

import CalloutCarouselBase from './CalloutCarouselBase';

import type { GetCalloutsQuery, GetCalloutsQueryVariables } from './__generated__/CalloutCarousel.gql';

const PAGE_SIZE = 12;

const GET_CALLOUTS = gql`
  query GetCallouts($pagination: PaginationInput!, $maxPriority: CalloutPriority, $filters: CalloutFiltersInput) {
    getCallouts(pagination: $pagination, maxPriority: $maxPriority, filters: $filters) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...CalloutDetails
        }
      }
    }
  }
  ${CalloutCard.Fragment}
`;

interface ICalloutCarouselProps {
  className?: string;
  maxPriority?: CalloutPriority;
  filters?: {
    merchantId?: string;
    financialAccountId?: string;
  };
  /** Called when the number of loaded callouts changes (including 0) */
  onCalloutCountChange?: (count: number) => void;
}

const CalloutCarousel = ({
  className,
  maxPriority = CalloutPriority.P1,
  filters,
  onCalloutCountChange,
}: ICalloutCarouselProps) => {
  const { data, loading, error, fetchMore } = useQuery<GetCalloutsQuery, GetCalloutsQueryVariables>(GET_CALLOUTS, {
    variables: {
      pagination: {
        limit: PAGE_SIZE,
        direction: PaginationDirection.Forward,
      },
      maxPriority,
      filters: filters
        ? {
            merchantId: filters.merchantId,
            financialAccountId: filters.financialAccountId,
          }
        : undefined,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { dismissCallout } = useUpdateCallout();

  const handleDismiss = React.useCallback(
    async (calloutId: string, appropriate?: boolean) => {
      await dismissCallout({ calloutId, appropriate });
    },
    [dismissCallout]
  );

  const handleLoadMore = React.useCallback(async () => {
    const pageInfo = data?.getCallouts?.pageInfo;
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      await fetchMore({
        variables: {
          pagination: {
            cursor: pageInfo.endCursor,
            limit: PAGE_SIZE,
            direction: PaginationDirection.Forward,
          },
        },
      });
    }
  }, [data, fetchMore]);

  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = React.useState(false);

  const navigateWithDateRange = useNavigateWithDateRange();
  const navigate = useNavigate();

  const handleCalloutClick = React.useCallback(
    (callout: Callout) => {
      // Handle unusual transaction callouts
      if (callout.transaction && callout.type === CalloutType.UnusualTransaction) {
        setSelectedTransactionId(callout.transaction.id);
        setIsSlideoutOpen(true);
      }
      // Handle unusual cumulative spend callouts - navigate to merchant, account, or spending page
      // with the date range set to the callout's relevant week (if metadata is valid)
      if (callout.type === CalloutType.UnusualCumulativeSpend) {
        // Build date range if we have valid metadata and event date
        let dateRange: IDateRange | undefined;
        if (isUnusualCumulativeSpendMetadata(callout.metadata) && callout.eventDate) {
          const eventDate = new Date(callout.eventDate);
          const startDate = new Date(eventDate);
          startDate.setDate(startDate.getDate() - 7); // Start 1 week before
          const endDate = new Date(eventDate);
          endDate.setDate(endDate.getDate() + 6); // End at Sunday of the callout week
          const formatDate = (date: Date) => date.toISOString().split('T')[0];
          dateRange = {
            start: { toString: () => formatDate(startDate) },
            end: { toString: () => formatDate(endDate) },
          };
        }

        // Navigate even without date params - better than silently failing
        if (callout.merchant?.id) {
          void navigate(PATHS.MERCHANT(callout.merchant.id, dateRange));
        } else if (callout.financialAccount?.id) {
          void navigate(PATHS.ACCOUNT(callout.financialAccount.id, dateRange));
        } else if (isUnusualCumulativeSpendMetadata(callout.metadata) && callout.metadata.subtype === 'global') {
          void navigate(PATHS.SPENDING(dateRange));
        }
      }
      // Handle high credit utilization callouts - navigate to account page
      if (callout.type === CalloutType.HighCreditUtilization && callout.financialAccount?.id) {
        void navigateWithDateRange(PATHS.ACCOUNT(callout.financialAccount.id));
      }
      // Handle low balance callouts - navigate to account page
      if (callout.type === CalloutType.LowBalance && callout.financialAccount?.id) {
        void navigateWithDateRange(PATHS.ACCOUNT(callout.financialAccount.id));
      }
      // Note: UnverifiedPhone callouts handle navigation via their own button
      // Note: Unhealthy bank connection callouts handle their own reconnection via PlaidLinkButton
    },
    [navigateWithDateRange, navigate]
  );

  const allCallouts: Callout[] =
    data?.getCallouts?.edges?.map((edge: GetCalloutsQuery['getCallouts']['edges'][number]) => edge.node) ?? [];
  const hasNextPage = data?.getCallouts?.pageInfo?.hasNextPage ?? false;
  const isLoadingMore = loading && allCallouts.length > 0;

  // Notify parent of callout count changes (once initial load completes)
  const initialLoadComplete = !loading || allCallouts.length > 0;
  React.useEffect(() => {
    if (initialLoadComplete) {
      onCalloutCountChange?.(allCallouts.length);
    }
  }, [allCallouts.length, initialLoadComplete, onCalloutCountChange]);

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading && allCallouts.length === 0) {
    return null;
  }

  if (allCallouts.length === 0) {
    return null;
  }

  return (
    <>
      <CalloutCarouselBase
        callouts={allCallouts}
        onDismiss={handleDismiss}
        className={className}
        onLoadMore={handleLoadMore}
        showLoadMore={hasNextPage}
        isLoadingMore={isLoadingMore}
        onCalloutClick={handleCalloutClick}
      />
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={isSlideoutOpen}
        onClose={() => setIsSlideoutOpen(false)}
      />
    </>
  );
};

export default CalloutCarousel;
